import { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	LinearProgress,
	MenuItem,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
	Tooltip,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import Sync from "@mui/icons-material/Sync";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import type { ComparisonResult, DifferenceItem, MatchOption } from "./models";
import { DIFFERENCE_TYPES } from "./models";
import { ComparisonValue } from "./ComparisonValue";
import { MatchOptionLabel } from "./MatchOptionLabel";
import { ReferenceLinksDialog } from "./ReferenceLinksDialog";
import { RollCutHistory } from "./RollCutHistory";
import { RollAssignmentHistory } from "./RollAssignmentHistory";
import {
	ReferenceActions,
	ReferenceDialog,
	ReferenceEvidence,
	ReferenceName,
	type ReferenceSelection,
} from "./ReferenceNames";
import {
	BarrelEvidence,
	BarrelMatchingSummary,
	BarrelMatchStatus,
	PendingBarrelMatches,
	PendingBarrelSummary,
} from "./BarrelEvidence";

export const displayDate = (value: string | null) =>
	value ? value.replace("T", " ") : "Date unavailable";

function cutRollName(item: DifferenceItem) {
	const roll = item.fields.find((field) => field.field === "Roll");
	return (
		Array.from(new Set([roll?.rollshop, roll?.mes].filter(Boolean))).join(
			" / ",
		) || "Unknown roll"
	);
}

function CutMatchGroup({
	item,
	columns,
}: {
	item: DifferenceItem;
	columns: number;
}) {
	return (
		<TableRow>
			<TableCell colSpan={columns} sx={{ bgcolor: "action.hover" }}>
				<Typography variant="body2">
					{cutRollName(item.matchOptions?.[0] ?? item)} · {item.rollshopCount}{" "}
					Rollshop {item.rollshopCount === 1 ? "record" : "records"} /{" "}
					{item.mesCount} MES {item.mesCount === 1 ? "record" : "records"}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{item.rollshopCount === 1 && item.mesCount > 1
						? "The same Rollshop record is compared with each MES record below."
						: item.mesCount === 1 && item.rollshopCount > 1
							? "Each Rollshop record below is compared with the same MES record."
							: item.rollshopCount === 0
								? "Separate MES records share the displayed date and diameter."
								: item.mesCount === 0
									? "Separate Rollshop records share the displayed date and diameter."
									: "Each row below compares one Rollshop record with one MES candidate."}
				</Typography>
			</TableCell>
		</TableRow>
	);
}

function CutRecord({
	item,
	side,
}: {
	item: DifferenceItem;
	side: "rollshop" | "mes";
}) {
	if ((side === "rollshop" ? item.rollshopCount : item.mesCount) === 0)
		return <Typography variant="body2">—</Typography>;
	const date = item.fields.find((field) => field.field === "Timestamp");
	const diameter = item.fields.find((field) => field.field === "Diameter");
	const paired = item.rollshopCount === 1 && item.mesCount === 1;
	const timestamp = date?.[side];
	return (
		<Stack gap={0.25} sx={{ whiteSpace: "nowrap" }}>
			<Typography
				variant="body2"
				fontWeight={paired && date?.different ? 600 : 400}
			>
				{timestamp?.slice(0, 10) ?? "Date unavailable"}
			</Typography>
			{side === "mes" && item.matchOption && timestamp?.includes("T") && (
				<Typography variant="caption" color="text.secondary">
					Time {timestamp.slice(11)}
				</Typography>
			)}
			<Typography
				variant="body2"
				fontWeight={paired && diameter?.different ? 600 : 400}
			>
				Ø {diameter?.[side] ?? "unavailable"}
			</Typography>
		</Stack>
	);
}

function ChangedFields({
	item,
	cuts = false,
}: {
	item: DifferenceItem;
	cuts?: boolean;
}) {
	const fields = item.fields.filter(
		(field) => field.different && !field.equivalentName,
	);
	const details = cuts ? [] : fields.filter((field) => field.informational);
	if (item.matchOption && fields.length === 0)
		return (
			<Typography variant="body2" color="success.main">
				Values match
			</Typography>
		);
	return (
		<Stack gap={0.25}>
			{fields
				.filter(
					(field) => cuts || (!field.informational && !field.mesMoreComplete),
				)
				.map((field) => (
					<Typography
						key={field.field}
						variant="body2"
						sx={{ overflowWrap: "anywhere" }}
					>
						{cuts ? (
							<>
								<Box component="span" fontWeight={600}>
									{field.field === "Timestamp" ? "Date" : field.field}
								</Box>
								<br />
								Rollshop:{" "}
								<ComparisonValue
									value={
										field.field === "Timestamp"
											? (field.rollshop?.slice(0, 10) ?? null)
											: field.rollshop
									}
								/>
								<br />
								MES:{" "}
								<ComparisonValue
									value={
										field.field === "Timestamp"
											? (field.mes?.slice(0, 10) ?? null)
											: field.mes
									}
								/>
							</>
						) : (
							<>
								{field.field} [
								<ComparisonValue value={field.rollshop} compact />,{" "}
								<ComparisonValue value={field.mes} compact />]
							</>
						)}
					</Typography>
				))}
			{details.length > 0 && (
				<Tooltip title={details.map((field) => field.field).join(", ")}>
					<Typography variant="caption" color="text.secondary">
						{details.length} secondary{" "}
						{details.length === 1 ? "difference" : "differences"} — see View
						values
					</Typography>
				</Tooltip>
			)}
			{item.matchOption &&
				fields
					.filter((field) => field.mesMoreComplete)
					.map((field) => (
						<Typography key={field.field} variant="body2" color="success.main">
							{field.field}: <ComparisonValue value={field.mes} compact /> —
							preserved (MES more complete)
						</Typography>
					))}
		</Stack>
	);
}

function MatchStatus({ item }: { item: DifferenceItem }) {
	const aligned = item.fields.every(
		(field) =>
			!field.different ||
			field.mesMoreComplete ||
			field.equivalentName ||
			field.informational,
	);
	const confirmed = item.matchOption?.confirmed;
	return (
		<Typography
			variant="body2"
			color={confirmed && aligned ? "success.main" : "info.main"}
		>
			{confirmed
				? aligned
					? "Match confirmed (OK)"
					: "Match confirmed — different values"
				: item.matchOption?.reference
					? "Reference pending"
					: "Match option"}
		</Typography>
	);
}

export function DifferenceDetails({
	result,
	dated,
	type,
	onTypeChange,
	disabled,
	stale,
	refreshing,
	onUpdate,
	onInvalidate,
	onRefresh,
}: {
	result: ComparisonResult;
	dated: boolean;
	type: string;
	onTypeChange: (type: string) => void;
	disabled: boolean;
	stale: boolean;
	refreshing: boolean;
	onInvalidate: (message: string, success: boolean) => void;
	onRefresh: () => void;
	onUpdate: (
		type: string,
		key: string | null,
		operation: "update" | "insert",
		matchOption?: MatchOption,
	) => void;
}) {
	const [selected, setSelected] = useState<DifferenceItem | null>(null);
	const cuts = result.concept === "roll-cuts";
	const rollName = selected?.fields.find(
		(field) => field.field === (cuts ? "Roll" : "Name"),
	);
	// An unmatched cut can still have earlier history in both systems.
	const rollshopName =
		rollName?.rollshop ?? (cuts ? rollName?.mes : null) ?? null;
	const mesName = rollName?.mes ?? (cuts ? rollName?.rollshop : null) ?? null;
	const [linkCategory, setLinkCategory] = useState(false);
	const references = (result.examples["Referenced names"] ?? []).flatMap(
		(item) => (item.reference ? [item.reference] : []),
	);
	const creatableReferences = references.filter(
		(reference) =>
			reference.missingSourceCatalog &&
			!reference.targetId &&
			reference.canCreate,
	).length;
	const [referenceSelection, setReferenceSelection] =
		useState<ReferenceSelection | null>(null);
	const [pagination, setPagination] = useState<{
		items: DifferenceItem[];
		page: number;
	} | null>(null);
	const [showAll, setShowAll] = useState(false);
	const [matchFilter, setMatchFilter] = useState("all");
	const barrelNames = type === "Barrel names";
	const insertCategory = type === "Only Rollshop";
	const items =
		result.examples[
			barrelNames && matchFilter !== "all" ? `${type}/${matchFilter}` : type
		] ?? [];
	const page =
		pagination?.items === items
			? Math.min(pagination.page, Math.max(0, Math.ceil(items.length / 50) - 1))
			: 0;
	const visibleItems = items.slice(page * 50, page * 50 + 50);
	const displayedItems = visibleItems.flatMap((item) => {
		const options = item.matchOptions?.length ? item.matchOptions : [item];
		return options.map((option, index) => ({
			item: option,
			group: cuts && item.matchOptions?.length && index === 0 ? item : null,
		}));
	});
	const cutMatchGroups =
		cuts && items.some((item) => item.matchOptions?.length);
	const countKey =
		DIFFERENCE_TYPES.find((option) => option.key === type)?.count ??
		"differenceItems";
	const relationCounts = {
		Consistent: result.barrelMatching?.consistent ?? 0,
		Possible: result.barrelMatching?.possible ?? 0,
		Ambiguous: result.barrelMatching?.ambiguous ?? 0,
	};
	const itemCount =
		barrelNames && matchFilter in relationCounts
			? relationCounts[matchFilter as keyof typeof relationCounts]
			: result[countKey];
	const columns = [
		...(barrelNames
			? ["Rollshop barrel", "MES barrel", "Supporting rollsets"]
			: cuts
				? ["Roll", "Rollshop date / diameter", "MES date / diameter"]
				: ["Item", ...(dated ? ["Latest business date"] : [])]),
		"Difference",
		cuts ? "Changes" : "Changed fields",
		"Actions",
	];
	return (
		<Box sx={{ p: 2, bgcolor: "background.default" }}>
			{result.concept === "roll-cuts" && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Cuts with the same roll, exactly equal diameters and dates within ±3
					calendar days can be updated individually or in bulk from Rollshop.
					Different diameters are listed for review and excluded from all
					updates.
				</Alert>
			)}
			{result.barrelMatching && (
				<BarrelMatchingSummary summary={result.barrelMatching} />
			)}
			{refreshing ? (
				<Box sx={{ mb: 2 }} role="status">
					<Typography variant="body2" sx={{ mb: 1 }}>
						Refreshing this category…
					</Typography>
					<LinearProgress aria-label="Refreshing category" />
				</Box>
			) : (
				stale && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Results may have changed. Refresh this category before updating MES.
					</Alert>
				)
			)}
			<Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
				<TextField
					id="rollshop-difference-type"
					select
					label="Difference type"
					size="small"
					value={type}
					disabled={disabled}
					onChange={(event) => onTypeChange(event.target.value)}
					sx={{ minWidth: 220 }}
				>
					{DIFFERENCE_TYPES.filter(
						(option) =>
							option.key !== "Barrel names" || result.concept === "barrels",
					).map((option) => (
						<MenuItem key={option.key} value={option.key}>
							{result.concept === "barrels" && option.key === "Only Rollshop"
								? "Unmatched Rollshop"
								: result.concept === "barrels" && option.key === "Only MES"
									? "Unmatched MES"
									: option.label}
						</MenuItem>
					))}
				</TextField>
				<Button
					size="small"
					variant="outlined"
					startIcon={<Sync />}
					disabled={disabled}
					onClick={onRefresh}
				>
					Refresh category
				</Button>
				{barrelNames && (
					<TextField
						id="rollshop-barrel-relationship"
						select
						label="Relationship"
						size="small"
						value={matchFilter}
						onChange={(event) => setMatchFilter(event.target.value)}
						sx={{ minWidth: 190 }}
					>
						<MenuItem value="all">All relationships</MenuItem>
						{Object.entries(relationCounts).map(([status, count]) => (
							<MenuItem key={status} value={status}>
								{status} ({count})
							</MenuItem>
						))}
					</TextField>
				)}
				<Typography variant="body2">
					Showing {visibleItems.length} of {itemCount.toLocaleString()}{" "}
					{barrelNames
						? "relationships"
						: cutMatchGroups
							? "comparison groups"
							: "differing items"}
					{cutMatchGroups && <> · {displayedItems.length} comparison rows</>}
					{(type === "all" || type === "Different values") && (
						<> · {result.differentFields.toLocaleString()} field differences</>
					)}
					{type === "all" && result.undated > 0 && (
						<> · {result.undated.toLocaleString()} undated</>
					)}
				</Typography>
				{!insertCategory && type !== "Referenced names" && (
					<Button
						variant="contained"
						startIcon={<Sync />}
						disabled={
							disabled ||
							stale ||
							!result.snapshotToken ||
							!(result.updatableRowsByCategory[type] > 0) ||
							result[countKey] === 0 ||
							![
								"all",
								"Different values",
								"MES more complete",
								"Barrel names",
								"Mapping issues",
							].includes(type) ||
							(barrelNames && !["all", "Consistent"].includes(matchFilter))
						}
						onClick={() => onUpdate(type, null, "update")}
					>
						Update MES for this category
					</Button>
				)}
				{result.concept !== "roll-cuts" &&
					(type === "all" || insertCategory || type === "Referenced names") && (
						<Button
							variant="contained"
							color="success"
							startIcon={<AddCircleOutline />}
							disabled={
								disabled ||
								stale ||
								!result.snapshotToken ||
								(result.insertableRows === 0 && creatableReferences === 0)
							}
							onClick={() => onUpdate("Only Rollshop", null, "insert")}
						>
							Insert in MES for this category (
							{result.insertableRows.toLocaleString()}
							{creatableReferences > 0
								? ` catalog + ${creatableReferences.toLocaleString()} references`
								: ""}
							)
						</Button>
					)}
				{references.length > 0 && (
					<Button
						variant="contained"
						startIcon={<LinkOutlined />}
						disabled={disabled || stale || !result.snapshotToken}
						onClick={() => setLinkCategory(true)}
					>
						Link to existing MES items
					</Button>
				)}
			</Stack>
			<Typography variant="caption" display="block" sx={{ mb: 1 }}>
				{result.mesMoreCompleteFields} field differences are OK because MES has
				more complete data. The OK category can overlap with other categories in
				mixed rows.
			</Typography>
			{items.some((item) => item.matchOptions?.length) && (
				<Typography
					variant="caption"
					color="info.main"
					display="block"
					sx={{ mb: 1 }}
				>
					{cuts
						? "Each group shows the record counts in both systems. Compare MES times and comments to distinguish cuts recorded on the same day. These groups are excluded from category updates."
						: "Review the matches below. Confirmed matches need no action when values are aligned. Link pending catalog names or update eligible values individually; these matches are excluded from category updates."}
				</Typography>
			)}
			<TableContainer
				key={`${type}:${matchFilter}:${page}`}
				sx={{ maxHeight: 480 }}
			>
				<Table stickyHeader size="small" aria-label="Differing items">
					<TableHead>
						<TableRow>
							{columns.map((label) => (
								<TableCell key={label}>{label}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{displayedItems.flatMap(({ item, group }) => [
							group ? (
								<CutMatchGroup
									key={`group:${group.key}`}
									item={group}
									columns={columns.length}
								/>
							) : null,
							<TableRow
								key={item.matchOption?.id ?? `${item.key}:${item.type}`}
							>
								<TableCell sx={{ overflowWrap: "anywhere", maxWidth: 300 }}>
									{cuts && !item.reference ? (
										cutRollName(item)
									) : item.matchOption ? (
										<MatchOptionLabel option={item.matchOption} />
									) : barrelNames ? (
										item.barrelMatch?.rollshopName
									) : item.barrelMatch ? (
										`${item.barrelMatch.rollshopName} / ${item.barrelMatch.mesName}`
									) : item.reference ? (
										<ReferenceName name={item.reference.name} />
									) : (
										item.key || "Missing business key"
									)}
								</TableCell>
								{barrelNames && (
									<TableCell sx={{ overflowWrap: "anywhere", maxWidth: 300 }}>
										{item.barrelMatch?.mesName}
									</TableCell>
								)}
								{cuts && (
									<>
										<TableCell>
											<CutRecord item={item} side="rollshop" />
										</TableCell>
										<TableCell>
											<CutRecord item={item} side="mes" />
										</TableCell>
									</>
								)}
								{(barrelNames || (dated && !cuts)) && (
									<TableCell sx={{ whiteSpace: "nowrap" }}>
										{barrelNames
											? item.barrelMatch?.rollsetCount
											: item.reference
												? "—"
												: displayDate(item.timestamp)}
									</TableCell>
								)}
								<TableCell>
									{item.barrelMatch && (
										<Box mb={0.5}>
											<BarrelMatchStatus match={item.barrelMatch} />
										</Box>
									)}
									{item.reference ? (
										<Typography
											variant="body2"
											color={
												item.reference.targetName ? "success.main" : "info.main"
											}
										>
											Referenced name
											{item.reference.targetName ? " (OK)" : ""}
										</Typography>
									) : item.informationalOnly ? (
										<Typography variant="caption" color="text.secondary">
											Secondary differences
										</Typography>
									) : item.type === "Notes" ? (
										<Typography variant="body2" color="info.main">
											Notes
										</Typography>
									) : item.barrelCandidates?.length ? (
										<Typography variant="body2" color="info.main">
											Match pending
										</Typography>
									) : item.matchOption ? (
										<MatchStatus item={item} />
									) : item.equivalentBarrel ||
										item.mesMoreComplete ||
										item.type === "Only MES" ? (
										<Stack
											direction="row"
											gap={0.5}
											alignItems="center"
											sx={{ color: "success.main" }}
										>
											<CheckCircleOutline fontSize="small" />
											<Typography variant="body2">
												{item.equivalentBarrel
													? "OK - equivalent barrel"
													: item.type === "Only MES"
														? "OK - only MES"
														: "OK - MES more complete"}
											</Typography>
										</Stack>
									) : item.barrelMatch?.accepted ? (
										"Different specifications"
									) : item.barrelMatch ? (
										"Review required"
									) : item.type === "Ambiguous" ? (
										<Typography variant="body2" color="info.main">
											Ambiguous
										</Typography>
									) : (
										item.type
									)}
								</TableCell>
								<TableCell sx={{ maxWidth: 380 }}>
									{item.reference ? (
										<ReferenceEvidence item={item.reference} />
									) : item.barrelCandidates?.length ? (
										<PendingBarrelSummary matches={item.barrelCandidates} />
									) : item.barrelMatch && !item.barrelMatch.accepted ? (
										<PendingBarrelSummary matches={[item.barrelMatch]} />
									) : item.type !== "Notes" &&
										item.rollshopCount === 1 &&
										item.mesCount === 1 ? (
										<ChangedFields item={item} cuts={cuts} />
									) : item.diagnostic ? null : (
										"—"
									)}
									{item.diagnostic && (
										<Typography variant="caption" display="block">
											{item.diagnostic}
										</Typography>
									)}
									{!item.matchOption &&
										!item.mesMoreComplete &&
										item.fields.some((field) => field.mesMoreComplete) && (
											<Typography
												variant="caption"
												color="success.main"
												display="block"
											>
												OK:{" "}
												{item.fields
													.filter((field) => field.mesMoreComplete)
													.map((field) => field.field)
													.join(", ")}{" "}
												— MES more complete
											</Typography>
										)}
								</TableCell>
								<TableCell>
									<Stack direction="row" gap={1} sx={{ whiteSpace: "nowrap" }}>
										<Button
											size="small"
											variant="outlined"
											startIcon={<VisibilityOutlined />}
											onClick={() => {
												setSelected(item);
												setShowAll(
													!item.fields.some((field) => field.different),
												);
											}}
										>
											{item.barrelCandidates?.length
												? "View relationships"
												: "View values"}
										</Button>
										{item.reference ? (
											<ReferenceActions
												item={item.reference}
												disabled={disabled || !result.snapshotToken}
												stale={stale}
												onSelect={setReferenceSelection}
											/>
										) : !item.matchOption || item.updateFields > 0 ? (
											<Tooltip
												title={
													stale
														? "Run comparison again."
														: ((item.type === "Only Rollshop"
																? item.insertUnavailableReason
																: item.updateUnavailableReason) ??
															"Preview changes to MES")
												}
											>
												<span>
													<Button
														size="small"
														variant="contained"
														color={
															item.type === "Only Rollshop"
																? "success"
																: "primary"
														}
														startIcon={
															item.type === "Only Rollshop" ? (
																<AddCircleOutline />
															) : (
																<Sync />
															)
														}
														disabled={
															disabled ||
															stale ||
															!result.snapshotToken ||
															(item.type === "Only Rollshop"
																? !item.canInsert
																: item.updateFields === 0)
														}
														onClick={() =>
															onUpdate(
																item.type === "Only Rollshop"
																	? "Only Rollshop"
																	: item.matchOption
																		? "Ambiguous"
																		: type,
																item.key,
																item.type === "Only Rollshop"
																	? "insert"
																	: "update",
																item.matchOption ?? undefined,
															)
														}
													>
														{item.type === "Only Rollshop"
															? "Insert in MES"
															: item.matchOption
																? "Update MES with this match"
																: "Update MES"}
													</Button>
												</span>
											</Tooltip>
										) : null}
										{item.matchOption?.reference &&
											!item.matchOption.confirmed && (
												<Button
													size="small"
													variant="contained"
													startIcon={<LinkOutlined />}
													disabled={disabled || stale || !result.snapshotToken}
													onClick={() => {
														const option = item.matchOption;
														if (option?.reference)
															setReferenceSelection({
																item: option.reference,
																operation: "link",
																suggestedTargetId: option.mesId ?? undefined,
															});
													}}
												>
													Link existing MES
												</Button>
											)}
									</Stack>
									{cuts &&
										item.mesCount > 0 &&
										item.rollshopCount > 0 &&
										item.updateUnavailableReason && (
											<Typography
												variant="caption"
												display="block"
												sx={{ mt: 0.5, maxWidth: 300 }}
											>
												{item.updateUnavailableReason}
											</Typography>
										)}
									{item.type === "Only Rollshop" &&
										!item.barrelCandidates?.length &&
										item.insertUnavailableReason && (
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ mt: 0.5, maxWidth: 300 }}
											>
												{item.insertUnavailableReason}
											</Typography>
										)}
								</TableCell>
							</TableRow>,
						])}
						{items.length === 0 && (
							<TableRow>
								<TableCell colSpan={columns.length}>
									No items in this category.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{items.length > 50 && (
				<TablePagination
					component="div"
					count={items.length}
					page={page}
					onPageChange={(_, value) => setPagination({ items, page: value })}
					rowsPerPage={50}
					rowsPerPageOptions={[50]}
					labelDisplayedRows={({ from, to, count }) =>
						`${from}–${to} of ${count} loaded items`
					}
				/>
			)}
			{linkCategory && references.length > 0 && result.snapshotToken && (
				<ReferenceLinksDialog
					items={references}
					snapshotToken={result.snapshotToken}
					onClose={() => setLinkCategory(false)}
					onInvalidate={onInvalidate}
				/>
			)}
			{referenceSelection && result.snapshotToken && (
				<ReferenceDialog
					item={referenceSelection.item}
					operation={referenceSelection.operation}
					suggestedTargetId={referenceSelection.suggestedTargetId}
					snapshotToken={result.snapshotToken}
					onClose={() => setReferenceSelection(null)}
					onInvalidate={onInvalidate}
				/>
			)}
			<Dialog
				open={selected !== null}
				onClose={() => setSelected(null)}
				fullWidth
				maxWidth="lg"
			>
				<DialogTitle>
					{selected?.reference ? (
						<ReferenceName name={selected.reference.name} />
					) : (
						selected?.key || "Difference details"
					)}
				</DialogTitle>
				<DialogContent>
					{selected?.reference ? (
						<ReferenceEvidence item={selected.reference} detailed />
					) : (
						<>
							{selected?.matchOption && (
								<MatchOptionLabel option={selected.matchOption} />
							)}
							{selected?.barrelCandidates?.length ? (
								<PendingBarrelMatches matches={selected.barrelCandidates} />
							) : null}
							{selected?.barrelMatch && (
								<BarrelEvidence match={selected.barrelMatch} />
							)}
							{selected?.diagnostic && (
								<Alert
									severity={selected.type === "Notes" ? "info" : "warning"}
								>
									{selected.diagnostic} Rollshop: {selected.rollshopCount} rows;
									MES: {selected.mesCount} rows.
								</Alert>
							)}
							{selected?.rollshopCount === 0 &&
								!selected.barrelCandidates?.length && (
									<Alert severity="info">
										This item is absent from Rollshop.
									</Alert>
								)}
							{selected?.mesCount === 0 &&
								!selected.barrelCandidates?.length && (
									<Alert severity="info">This item is absent from MES.</Alert>
								)}
							{selected?.type !== "Notes" && (
								<FormControlLabel
									control={
										<Switch
											checked={showAll}
											onChange={(_, checked) => setShowAll(checked)}
										/>
									}
									label="Show unchanged fields"
								/>
							)}
							<TableContainer>
								<Table
									size="small"
									aria-label="Rollshop and MES values"
									sx={
										result.concept === "rolls" || cuts
											? { tableLayout: "fixed", minWidth: 800 }
											: undefined
									}
								>
									{(result.concept === "rolls" || cuts) && (
										<colgroup>
											<col style={{ width: "20%" }} />
											<col style={{ width: "24%" }} />
											<col style={{ width: "24%" }} />
											<col style={{ width: "32%" }} />
										</colgroup>
									)}
									<TableHead>
										<TableRow>
											{[
												"Field",
												selected?.barrelCandidates?.length &&
												selected.rollshopCount === 0
													? "Rollshop (match pending)"
													: "Rollshop",
												selected?.barrelCandidates?.length &&
												selected.mesCount === 0
													? "MES (match pending)"
													: "MES",
												"Assessment",
											].map((label) => (
												<TableCell key={label}>{label}</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{selected?.fields
											.filter(
												(field) =>
													selected.type === "Notes" ||
													showAll ||
													field.different,
											)
											.map((field) => (
												<TableRow
													key={field.field}
													sx={{
														bgcolor:
															selected.type !== "Notes" &&
															field.different &&
															!field.informational
																? "action.hover"
																: undefined,
													}}
												>
													<TableCell>{field.field}</TableCell>
													<TableCell
														sx={{
															whiteSpace: "pre-wrap",
															overflowWrap: "anywhere",
															maxWidth: 350,
														}}
													>
														{selected.rollshopCount === 0 ? (
															"-"
														) : (
															<ComparisonValue value={field.rollshop} />
														)}
													</TableCell>
													<TableCell
														sx={{
															whiteSpace: "pre-wrap",
															overflowWrap: "anywhere",
															maxWidth: 350,
														}}
													>
														{selected.mesCount === 0 ? (
															"-"
														) : (
															<ComparisonValue value={field.mes} />
														)}
													</TableCell>
													<TableCell>
														{selected.type === "Notes" ? (
															<Typography variant="body2" color="info.main">
																Informational
															</Typography>
														) : selected.barrelCandidates?.length ? (
															<Typography variant="body2" color="info.main">
																Match pending
															</Typography>
														) : selected.type === "Only MES" ? (
															<Typography variant="body2" color="success.main">
																OK - MES-only data
															</Typography>
														) : field.equivalentName ? (
															<Typography variant="body2" color="success.main">
																Equivalent barrel - MES name preserved
															</Typography>
														) : field.mesMoreComplete ? (
															<Typography variant="body2" color="success.main">
																✓ OK - MES more complete
															</Typography>
														) : field.different && field.informational ? (
															<Typography
																variant="caption"
																color="text.secondary"
															>
																{field.updateUnavailableReason
																	? "Optional catalog link unavailable. Existing MES value is kept; new records can leave it empty."
																	: "Optional catalog detail; does not block importing the record."}
															</Typography>
														) : field.different ? (
															(field.updateUnavailableReason ??
															"Different value")
														) : (
															"Equivalent"
														)}
													</TableCell>
												</TableRow>
											))}
									</TableBody>
									{(result.concept === "rolls" || cuts) &&
										selected &&
										(rollshopName || mesName) && (
											<>
												<RollCutHistory
													key={`cuts:${rollshopName}:${mesName}`}
													rollshopName={rollshopName}
													mesName={mesName}
												/>
												{result.concept === "rolls" && (
													<RollAssignmentHistory
														key={`assignments:${rollshopName}:${mesName}`}
														rollshopName={rollshopName}
														mesName={mesName}
													/>
												)}
											</>
										)}
								</Table>
							</TableContainer>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setSelected(null)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
